import type { MeResponse } from "@media-viewer/shared";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { ChangePasswordModal } from "./ChangePasswordModal";

interface LayoutProps {
  me: MeResponse;
  current: string;
  appName: string;
  /** Authentik (or other IdP) self-service account URL. Null when OIDC isn't configured. */
  accountUrl: string | null;
  authMode: "oidc" | "local" | "dev";
  aiEnabled: boolean;
  children: ReactNode;
}

const NAV = [
  { href: "/", label: "All" },
  { href: "/photos", label: "Photos" },
  { href: "/videos", label: "Videos" },
  { href: "/favorites", label: "Favorites" },
  { href: "/playlists", label: "Playlists" },
  { href: "/tags", label: "Tags" },
  { href: "/upload", label: "Upload" },
];

export function Layout({ me, current, appName, accountUrl, authMode, aiEnabled, children }: LayoutProps) {
  const qc = useQueryClient();
  const nav = aiEnabled ? [...NAV, { href: "/generate", label: "Generate" }] : NAV;
  const aiStatus = useQuery({ queryKey: ["ai", "status"], queryFn: api.ai.status, enabled: aiEnabled });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  const surpriseKind = useMutation({
    mutationFn: ({ kind }: { kind: "photo" | "video" }) => api.random({ kind }),
    onSuccess: (data, vars) => {
      const setParam = vars.kind === "photo" ? "photos" : "videos";
      window.location.href = `/media/${data.id}?set=${setParam}`;
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: ["media"] });
    },
  });

  // Close the drawer when route changes
  useEffect(() => {
    setMobileNavOpen(false);
  }, [current]);

  // Esc closes the drawer
  useEffect(() => {
    if (!mobileNavOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileNavOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  function navItem(item: { href: string; label: string }) {
    const active =
      item.href === "/"
        ? current === "/"
        : current === item.href || current.startsWith(item.href + "/");
    return (
      <a
        key={item.href}
        href={item.href}
        className={
          "block rounded px-3 py-1.5 transition-colors " +
          (active
            ? "bg-zinc-800 text-zinc-100"
            : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200")
        }
      >
        {item.label}
      </a>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="relative flex shrink-0 items-center justify-between gap-2 border-b border-zinc-800 bg-zinc-950 px-3 py-3 sm:px-6">
        {/* Brand + desktop nav */}
        <div className="flex min-w-0 items-center gap-3 sm:gap-6">
          <a href="/" className="truncate text-base font-semibold tracking-tight">
            {appName}
          </a>
          <nav className="hidden items-center gap-1 text-sm md:flex">
            {nav.map(navItem)}
            {me.isAdmin && (
              <a
                href="/admin"
                className={
                  "rounded px-3 py-1.5 transition-colors " +
                  (current.startsWith("/admin")
                    ? "bg-amber-700/30 text-amber-100"
                    : "text-amber-300/70 hover:bg-amber-700/20 hover:text-amber-100")
                }
              >
                Admin
              </a>
            )}
          </nav>
        </div>

        {/* Right side: random buttons always visible; user info hides on mobile (lives in drawer instead) */}
        <div className="flex items-center gap-1.5 text-sm text-zinc-400 sm:gap-3">
          <button
            onClick={() => surpriseKind.mutate({ kind: "photo" })}
            disabled={surpriseKind.isPending}
            title="Show a random photo"
            className="rounded bg-emerald-700/30 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-700/50 disabled:opacity-50 sm:px-2.5"
          >
            🎲<span className="ml-1 hidden sm:inline">Photo</span>
          </button>
          <button
            onClick={() => surpriseKind.mutate({ kind: "video" })}
            disabled={surpriseKind.isPending}
            title="Play a random video"
            className="rounded bg-sky-700/30 px-2 py-1 text-xs text-sky-200 hover:bg-sky-700/50 disabled:opacity-50 sm:px-2.5"
          >
            🎲<span className="ml-1 hidden sm:inline">Video</span>
          </button>

          {aiStatus.data && !aiStatus.data.unlimited && (
            <a
              href="/generate"
              title="AI image credits left this month"
              className="hidden whitespace-nowrap rounded-full border border-indigo-800 bg-indigo-950 px-2.5 py-0.5 text-xs text-indigo-200 hover:bg-indigo-900 xl:inline"
            >
              {`${aiStatus.data.remaining} / ${aiStatus.data.allowance} credits`}
            </a>
          )}

          {/* Desktop: user info inline */}
          <div className="hidden items-center gap-3 md:flex">
            <span className="ml-2 max-w-[14ch] truncate" title={me.email ?? ""}>
              {me.displayName ?? me.email ?? `user-${me.id}`}
            </span>
            {me.isAdmin && (
              <span className="rounded bg-amber-700/40 px-2 py-0.5 text-xs text-amber-100">
                admin
              </span>
            )}
            {accountUrl && (
              <a
                href={accountUrl}
                target="_blank"
                rel="noreferrer"
                className="hover:text-zinc-200"
                title="Manage your account / change password"
              >
                Account
              </a>
            )}
            {authMode === "local" && me.username && (
              <button onClick={() => setChangePwOpen(true)} className="hover:text-zinc-200">
                Change password
              </button>
            )}
            <a href="/api/auth/logout" className="hover:text-zinc-200">
              Sign out
            </a>
          </div>

          {/* Mobile: hamburger toggle */}
          <button
            onClick={() => setMobileNavOpen((on) => !on)}
            aria-label="Toggle menu"
            aria-expanded={mobileNavOpen}
            className="ml-1 rounded bg-zinc-800 px-2.5 py-1 text-zinc-200 hover:bg-zinc-700 md:hidden"
          >
            {mobileNavOpen ? "✕" : "☰"}
          </button>
        </div>

        {/* Mobile drawer — slides under the header */}
        {mobileNavOpen && (
          <div className="absolute left-0 right-0 top-full z-30 border-b border-zinc-800 bg-zinc-950 shadow-xl md:hidden">
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3 text-xs text-zinc-400">
              <span className="truncate" title={me.email ?? ""}>
                {me.displayName ?? me.email ?? `user-${me.id}`}
              </span>
              <div className="flex items-center gap-2">
                {me.isAdmin && (
                  <span className="rounded bg-amber-700/40 px-2 py-0.5 text-[10px] text-amber-100">
                    admin
                  </span>
                )}
                {accountUrl && (
                  <a
                    href={accountUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-zinc-200"
                  >
                    Account
                  </a>
                )}
                {authMode === "local" && me.username && (
                  <button onClick={() => setChangePwOpen(true)} className="hover:text-zinc-200">
                    Change password
                  </button>
                )}
                <a href="/api/auth/logout" className="hover:text-zinc-200">
                  Sign out
                </a>
              </div>
            </div>
            <nav className="flex flex-col p-2 text-sm">
              {nav.map(navItem)}
              {me.isAdmin && (
                <a
                  href="/admin"
                  className={
                    "block rounded px-3 py-1.5 transition-colors " +
                    (current.startsWith("/admin")
                      ? "bg-amber-700/30 text-amber-100"
                      : "text-amber-300/70 hover:bg-amber-700/20 hover:text-amber-100")
                  }
                >
                  Admin
                </a>
              )}
            </nav>
          </div>
        )}
      </header>
      <main className="flex-1 overflow-auto">{children}</main>
      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </div>
  );
}
